'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { AdminUser, AuthState } from '@/types/auth';
import { fetchFromAdminApi } from '@/lib/api-client';

interface AuthContextType extends AuthState {
  hasPermission: (permission: string) => boolean;
  loginWithPlaceholder: (actor?: string, rolePreset?: 'superadmin' | 'pm' | 'auditor' | 'readonly') => Promise<void>;
  logout: () => void;
  setAuthSession: (token: string, user: AdminUser) => void;
}

const STORAGE_KEY_TOKEN = 'imarket_admin_token';
const STORAGE_KEY_USER = 'imarket_admin_user';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const logout = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY_TOKEN);
      localStorage.removeItem(STORAGE_KEY_USER);
    }
    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, []);

  const setAuthSession = useCallback((token: string, user: AdminUser) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_TOKEN, token);
      localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(user));
    }
    setState({
      user,
      token,
      isAuthenticated: true,
      isLoading: false,
    });
  }, []);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!state.user || !state.isAuthenticated) {
        return false; // Deny by default
      }
      return state.user.permissions.includes(permission);
    },
    [state.user, state.isAuthenticated]
  );

  const loginWithPlaceholder = useCallback(
    async (actor = 'admin@imarket.local', rolePreset: 'superadmin' | 'pm' | 'auditor' | 'readonly' = 'superadmin') => {
      setState((prev) => ({ ...prev, isLoading: true }));

      // Permissions mapped per role preset
      let permissions: string[] = [];
      if (rolePreset === 'superadmin') {
        permissions = [
          'system:health:read',
          'system:metrics:read',
          'admin:audit:read',
          'users:read',
          'organizations:read',
          'brands:read',
          'credits:read',
          'credits:grant',
          'logs:read',
          'jobs:read',
          'campaigns:read',
          'dashboard:read',
        ];
      } else if (rolePreset === 'pm') {
        permissions = [
          'dashboard:read',
          'users:read',
          'organizations:read',
          'brands:read',
          'credits:read',
          'credits:grant',
          'logs:read',
          'jobs:read',
          'campaigns:read',
          'admin:audit:read',
          'system:health:read',
        ];
      } else if (rolePreset === 'auditor') {
        permissions = ['system:health:read', 'admin:audit:read', 'logs:read'];
      } else {
        permissions = ['system:health:read', 'users:read', 'organizations:read', 'brands:read', 'credits:read'];
      }

      const mockUser: AdminUser = {
        userId: `usr-${actor.split('@')[0]}`,
        email: actor,
        displayName: actor.split('@')[0].toUpperCase(),
        permissions,
      };

      // Call backend auth boundary placeholder endpoint
      try {
        const res = await fetchFromAdminApi<{ token: string; actor: string }>('/api/admin/auth/login-placeholder', {
          method: 'POST',
          body: JSON.stringify({ username: actor }),
        });

        const token = res.data?.token || 'dev-full-access-token';
        setAuthSession(token, mockUser);
      } catch {
        // Fallback to local session in offline/development mode
        setAuthSession('dev-full-access-token', mockUser);
      }
    },
    [setAuthSession]
  );

  // Restore session after mount so SSR never stays on "Validating admin credentials..."
  // if client JS is delayed. isLoading starts true on the server and clears here.
  useEffect(() => {
    let cancelled = false;
    const finish = (next: AuthState) => {
      if (!cancelled) {
        setState(next);
      }
    };

    try {
      const storedToken = localStorage.getItem(STORAGE_KEY_TOKEN);
      const storedUser = localStorage.getItem(STORAGE_KEY_USER);

      if (storedToken && storedUser) {
        const parsedUser = JSON.parse(storedUser) as AdminUser;
        finish({
          user: parsedUser,
          token: storedToken,
          isAuthenticated: true,
          isLoading: false,
        });
        return () => {
          cancelled = true;
        };
      }
    } catch {
      // Fall through to unauthenticated
    }

    finish({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        hasPermission,
        loginWithPlaceholder,
        logout,
        setAuthSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

